import { getImageUrl } from '@/lib/image';
import { cn } from '@/lib/utils';
import { type Site } from '@/types/search';
import { Check, MapPin } from 'lucide-react';
import { useState } from 'react';
import { CARD, ROW } from './search-bar.styles';

/**
 * Resolves a site's artwork, best source first.
 *
 * `featured_image` is the intended field but is populated on roughly half the
 * rows, and `is_featured` is not correlated with having one — so a missing image
 * is the ordinary case here, not an edge case. `location.image` is the tier that
 * actually fires today; the initials tile below it exists so a site can never
 * render as a broken <img>.
 */
function resolveImageSources(site: ThumbnailSite): string[] {
  const sources: string[] = [];
  if (site.featured_image) sources.push(getImageUrl(site.featured_image, 'media'));
  if (site.location?.image) sources.push(getImageUrl(site.location.image, 'location'));
  return sources;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Structural minimum, so the ladder is reusable by anything that has a site-shaped
 * record — the voucher detail page's applicable-sites grid, for one — without
 * needing the full `Site` payload.
 */
export type ThumbnailSite = Pick<Site, 'name'> & {
  featured_image?: string | null;
  location?: { image?: string | null } | null;
};

interface SiteThumbProps {
  site: ThumbnailSite;
  className?: string;
  /** Rendered instead of an image once every source has failed. */
  fallbackClassName?: string;
}

/** Walks the source list on error, then degrades to an initials tile. */
export function SiteThumb({ site, className, fallbackClassName }: SiteThumbProps) {
  const sources = resolveImageSources(site);
  const [attempt, setAttempt] = useState(0);
  const src = sources[attempt];

  if (!src) {
    return (
      <div className={cn('bg-surface-muted text-muted-foreground flex items-center justify-center', className, fallbackClassName)}>
        <span className="text-sm font-semibold tracking-wide">{initialsOf(site.name)}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setAttempt((current) => current + 1)}
      className={cn('bg-surface-muted', className)}
    />
  );
}

interface DestinationItemProps {
  site: Site;
  isSelected: boolean;
  onSelect: (site: Site) => void;
}

/** The image tile used by the card grid. */
export function DestinationCard({ site, isSelected, onSelect }: DestinationItemProps) {
  return (
    <button
      type="button"
      data-selected={isSelected}
      onClick={() => onSelect(site)}
      aria-label={site.location?.name ? `${site.name}, ${site.location.name}` : site.name}
      className={CARD.base}
    >
      <SiteThumb site={site} className={CARD.media} fallbackClassName="aspect-[4/3] w-full" />

      <span className={CARD.scrim}>
        <span className={CARD.title}>{site.name}</span>
        {site.location?.name && <span className={cn('block', CARD.subtitle)}>{site.location.name}</span>}
      </span>

      {isSelected && (
        <span className={CARD.check} aria-hidden="true">
          <Check className="size-3.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

/** The compact row used by the long-list sections and by search results. */
export function DestinationRow({ site, isSelected, onSelect }: DestinationItemProps) {
  const meta = site.location?.name ?? site.address ?? undefined;

  return (
    <button
      type="button"
      data-selected={isSelected}
      onClick={() => onSelect(site)}
      aria-label={meta ? `${site.name}, ${meta}` : site.name}
      className={ROW.base}
    >
      <SiteThumb site={site} className={ROW.thumb} fallbackClassName="size-11 rounded-lg" />

      <span className="min-w-0 flex-1">
        <span className={cn('block', ROW.name)}>{site.name}</span>
        {meta && <span className={cn('block', ROW.meta)}>{meta}</span>}
      </span>

      {isSelected ? (
        <Check className="text-primary size-4 shrink-0" strokeWidth={3} />
      ) : (
        <MapPin className="text-muted-foreground size-4 shrink-0" />
      )}
    </button>
  );
}
